// client/src/components/react-dashboard/store-management/ProductList.jsx

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  AddCircleOutline,
  ArchiveOutlined,
  DeleteForeverOutlined,
  EditOutlined,
  ImageOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import { getProductId, normalizeImageUrl } from './productUtils';

const ProductList = ({
  colors,
  products,
  isProductsLoading,
  isProductsFetching,
  isProductsError,
  productsError,
  isArchiving,
  isHardDeleting,
  onRefresh,
  onNewProduct,
  onEditProduct,
  onArchiveProduct,
  onHardDeleteProduct,
}) => {
  const columns = [
    {
      field: 'image',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const image =
          params.row?.primaryImage ||
          params.row?.images?.find((item) => item.isPrimary) ||
          params.row?.images?.[0];

        const imageUrl = normalizeImageUrl(image);

        return imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={params.row.name}
            sx={{
              width: 42,
              height: 42,
              objectFit: 'cover',
              borderRadius: '6px',
            }}
          />
        ) : (
          <ImageOutlined sx={{ color: colors.grey[400] }} />
        );
      },
    },
    {
      field: 'name',
      headerName: 'Product',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'slug',
      headerName: 'Slug',
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 130,
      renderCell: (params) => <Chip label={params.value} size="small" />,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const color =
          params.value === 'active'
            ? 'success'
            : params.value === 'archived'
              ? 'error'
              : 'warning';

        return <Chip label={params.value} size="small" color={color} />;
      },
    },
    {
      field: 'featured',
      headerName: 'Featured',
      width: 110,
      renderCell: (params) =>
        params.value ? (
          <Chip label="Yes" size="small" color="success" />
        ) : (
          <Chip label="No" size="small" />
        ),
    },
    {
      field: 'pricing',
      headerName: 'Pricing',
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => params.row?.pricing?.displayPriceText || '',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onEditProduct(params.row);
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>

          <Tooltip title="Archive">
            <IconButton
              size="small"
              color="warning"
              disabled={isArchiving}
              onClick={(event) => {
                event.stopPropagation();
                onArchiveProduct(params.row);
              }}
            >
              <ArchiveOutlined />
            </IconButton>
          </Tooltip>

          <Tooltip title="Hard delete">
            <IconButton
              size="small"
              color="error"
              disabled={isHardDeleting}
              onClick={(event) => {
                event.stopPropagation();
                onHardDeleteProduct(params.row);
              }}
            >
              <DeleteForeverOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Paper sx={{ p: 2, backgroundColor: colors.primary[400] }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Store Products
          </Typography>
          <Typography variant="body2" color={colors.grey[300]}>
            View, edit, archive, and delete MongoDB-backed store products.
          </Typography>
        </Box>

        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={onRefresh}
            disabled={isProductsFetching}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={onNewProduct}
          >
            New Store Item
          </Button>
        </Box>
      </Box>

      {isProductsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {productsError?.data?.message || productsError?.message || 'Failed to load products.'}
        </Alert>
      )}

      <Box height="650px">
        <DataGrid
          rows={products}
          columns={columns}
          getRowId={(row) => getProductId(row)}
          loading={isProductsLoading || isProductsFetching}
          disableSelectionOnClick
          onRowDoubleClick={(params) => onEditProduct(params.row)}
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: colors.blueAccent?.[700] || colors.primary[500],
              borderBottom: 'none',
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: colors.primary[400],
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: 'none',
              backgroundColor: colors.blueAccent?.[700] || colors.primary[500],
            },
            '& .MuiCheckbox-root': {
              color: `${colors.greenAccent[200]} !important`,
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default ProductList;